import { getAgentApiHost } from "@/lib/agent-api-host";
import {
  brandSearchFailure,
  validateBrandText,
  validateSourceBrands,
} from "@/lib/brand-search-state";

import { assertCurrentBrowserSession, checkBrowserSessionResponse } from "@/lib/browser-session";
import { decodeFirebaseJwt } from "@/lib/jwt-decode";
import type {
  TaxonomyAndAttributesResponse,
  TaxonomyTreeResponse,
  AttributesDataResponse,
} from "@/types/taxonomy";
import { isCategoryToken, type CategoryToken } from "@/utils/genderMapping";

import { getAuthToken } from "../../openapi/auth";
import { client as shopAgentClient } from "../../openapi/shop_agent/client/client.gen";
import {
  addItemsToCartShopAgentCartsCartIdItemsPost,
  createCartShopAgentCartsPost,
  createCollectionShopAgentCollectionsPost, // User endpoints
  createSavedBrandShopAgentSavedBrandsPost,
  createSavedSearchShopAgentSavedSearchesPost,
  createUserShopAgentUsersMePost,
  deleteCollectionShopAgentCollectionsCollectionIdDelete,
  deleteCosimoAgentThreadShopAgentThreadsCosimoAgentThreadIdDelete,
  deleteSavedBrandShopAgentSavedBrandsSavedBrandIdDelete,
  deleteSavedSearchShopAgentSavedSearchesSavedSearchIdDelete,
  getAttributesByFacetsShopAgentCategoryAttributesAggregatedAttributesByFacetsPost,
  getCategoryCountsShopAgentCategoryAttributesCategoryCountsPost,
  getCartShopAgentCartsCartIdGet,
  getCollectionShopAgentCollectionsCollectionIdGet,
  getCosimoAgentThreadsShopAgentThreadsCosimoAgentGet,
  getSavedBrandByNameShopAgentSavedBrandsByNameBrandNameGet,
  getShopAgentChatHistoryShopAgentThreadsShopAgentThreadIdGet, // Thread endpoints
  getUserCartsShopAgentCartsGet,
  getUserShopAgentUsersMeGet,
  listCollectionsForUserShopAgentCollectionsGet,
  listConnectedAppsShopAgentMcpConnectedAppsGet,
  listSavedBrandsForUserShopAgentSavedBrandsGet,
  listSavedSearchesForUserShopAgentSavedSearchesGet,
  removeItemsFromCartShopAgentCartsCartIdItemsDelete,
  revokeConnectedAppShopAgentMcpConnectedAppsDelete,
  shopAgentBrandGetByName,
  shopAgentBrandDiscoveryGetDetails,
  shopAgentBrandIndexPrefixSearch,
  shopAgentBrandIndexSearchByBrands,
  shopAgentBrandIndexSearchFromQuery,
  shopAgentBrandGetShoppingOptions,
  shopAgentBrandIndexSearchFromText, // Brand endpoints
  shopAgentBrandListBrands,
  shopAgentCatalogGetProductGroupByUuid,
  shopAgentCatalogHydrateProducts,
  shopAgentGetSearchPage,
  shopAgentSearchProducts,
  shopAgentSearchSimilarProducts,
  suggestAttributesShopAgentAttributeAgentSuggestPost,
  getSearchQueryShopAgentSearchQueriesSearchIdGet,
  getThreadLatestQueryShopAgentSearchQueriesThreadsThreadIdLatestGet,
  listUserSearchQueriesShopAgentSearchQueriesGet,
  updateCartShopAgentCartsCartIdPut,
  updateCollectionShopAgentCollectionsCollectionIdPatch,
  updateSavedBrandShopAgentSavedBrandsSavedBrandIdPatch,
  updateSavedSearchShopAgentSavedSearchesSavedSearchIdPatch,
  updateUserPreferencesShopAgentUsersMePreferencesPreferenceTypePatch,
  updateUserShopAgentUsersMePatch,
} from "../../openapi/shop_agent/client/sdk.gen";
import {
  type AggregateOffer,
  type AssistantAgentResponse,
  type AttributeCount,
  type AttributeInfo,
  type AttributeRecommendation,
  type AttributeSuggestion,
  type BrandIndex,
  type BrandDiscoveryDetails,
  type BrandIndexSearchResponse,
  type BrandIndexSearchResult,
  type BrandRecommendation,
  type BrandResult,
  type BrandShoppingOptionsResponse,
  type BrandTopCategoriesResponse,
  type BrandTopCategory,
  type Cart,
  type CartUpsert,
  type CategoryAttributesByFacetsRequest,
  type CategoryCountsRequest,
  type CategoryCountsResponse,
  type ChatHistory,
  type ChatMessage,
  type Collection,
  type CollectionCreate,
  type CollectionUpdate,
  type ConnectedApp,
  type CosimoAgentResponse,
  type GetAttributeValuesResponse,
  type HydratedProduct,
  type ProductDetailResponse,
  type MerchantProductListItem,
  type ProductEnrichment,
  type SearchProduct,
  type SearchQueryItem,
  type SearchQueryListResponse,
  type SearchWithRecommendations,
  type ShopperSearchPage,
  type ShopperSearchRequest,
  type SimilarProductsRequest,
  type SimilarProductsResponse,
  type StoredSearch,
  type SavedBrand,
  type SavedBrandCreate,
  type SavedBrandUpdate,
  type SavedSearch,
  type SavedSearchCreate,
  type SavedSearchUpdate,
  type ShopAgentEndEvent,
  type ShopAgentErrorEvent,
  type ShopAgentMessageEvent,
  type ShopAgentMetadataEvent,
  type ShopAgentSearchEvent,
  type ShopAgentToolEvent,
  type Thread,
  type User,
  type UserCreate,
  type UserPreferenceUpdate,
  type UserUpdate,
  type VariantSpec,
} from "../../openapi/shop_agent/client/types.gen";
import type { CuratedQuery } from "@/lib/curated-queries";
import type { ShopperFilters, ShopperSearchSurface } from "@/lib/shopper-search";

// Re-export types for consumers
export type {
  AggregateOffer,
  AssistantAgentResponse,
  AttributeInfo,
  AttributeRecommendation,
  BrandIndexSearchResponse,
  BrandIndexSearchResult,
  BrandRecommendation,
  BrandResult,
  Cart,
  CategoryCountsResponse,
  ChatHistory,
  ChatMessage,
  Collection,
  ConnectedApp,
  CosimoAgentResponse,
  GetAttributeValuesResponse,
  ProductEnrichment,
  SearchProduct,
  SearchQueryItem,
  SearchQueryListResponse,
  SearchWithRecommendations,
  SavedBrand,
  SavedBrandCreate,
  SavedBrandUpdate,
  SavedSearch,
  SavedSearchCreate,
  SavedSearchUpdate,
  StoredSearch,
  Thread,
};

// Re-export taxonomy types (fetched from CDN, not API)
export type { TaxonomyAndAttributesResponse, TaxonomyTreeResponse, AttributesDataResponse };

// Note: shopAgentClient is now imported from the generated client.gen.ts
// It automatically includes Firebase authentication via the api-client-config.ts
// No need for manual interceptors or client initialization

// Export the client for use throughout the app
export { shopAgentClient };

/**
 * Determines if the API host is a local backend (localhost or 127.0.0.1)
 * Used for functions that make direct fetch calls (streaming, reset endpoints)
 */
function isLocalBackend(apiUrl: string): boolean {
  try {
    const url = new URL(apiUrl);
    return url.hostname === "localhost" || url.hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

/**
 * Build auth headers for direct fetch calls.
 * Matches the auth pattern from api-client-config.ts:
 * - Always sets Authorization header with Bearer token
 * - For local backends, also decodes JWT and sets X-Octogen-User-ID header
 */
async function buildAuthHeaders(baseUrl: string): Promise<HeadersInit> {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;

    // For local backend, also set X-Octogen-User-ID header directly
    // This allows the backend to work without EnvoyGateway
    if (isLocalBackend(baseUrl)) {
      const decoded = decodeFirebaseJwt(token);
      if (decoded?.uid) {
        headers["X-Octogen-User-ID"] = decoded.uid;
      } else {
        console.warn(
          "Local auth mode: Failed to decode Firebase JWT - X-Octogen-User-ID header not set. " +
            "API calls will fail with 401. Check that the token is a valid Firebase JWT."
        );
      }
    }
  }

  return headers;
}

async function sessionFetch(url: string, init: RequestInit): Promise<Response> {
  const token = new Headers(init.headers).get("Authorization")?.replace(/^Bearer /, "");
  const uid = token ? decodeFirebaseJwt(token)?.uid : undefined;
  assertCurrentBrowserSession(uid);
  return checkBrowserSessionResponse(await fetch(url, init), uid);
}

/**
 * Age groups mapping by taxonomy depth0 value.
 */
export const AGE_GROUPS_BY_DEPTH0: Record<string, string[] | null> = {
  "adult clothing": ["adult"],
  "kid's clothing": ["infant", "toddler", "kids"],
  beauty: null, // age-neutral
};

/**
 * Age groups mapping by navbar category.
 * Used for inferring age_groups in MLT searches and other contexts.
 * Includes aliases (e.g., "Kids" for "Kidswear") for robustness.
 */
export const AGE_GROUPS_BY_NAVBAR_CATEGORY: Record<string, string[]> = {
  Menswear: ["adult"],
  Womenswear: ["adult"],
  Kidswear: ["infant", "toddler", "kids"],
  Kids: ["infant", "toddler", "kids"], // Alias
};

/**
 * Gender mapping by navbar category.
 * Used for inferring gender in MLT searches and other contexts.
 */
export const GENDER_BY_NAVBAR_CATEGORY: Record<string, string> = {
  Menswear: "men",
  Womenswear: "women",
};

// DEPRECATED: Stub functions for backwards compatibility with deprecated components
export const getThreadsHistoryThreadsUserIdGet = async (): Promise<never> => {
  throw new Error(
    "Thread endpoints are deprecated. Use getShopAgentThreads or getCosimoAgentThreads instead."
  );
};

export const removeThreadHistoryThreadsUserIdThreadIdDelete = async (): Promise<never> => {
  throw new Error(
    "Thread endpoints are deprecated. Use deleteShopAgentThread or deleteCosimoAgentThread instead."
  );
};

export const getChatHistoryHistoryThreadsUserIdThreadIdGet = async (): Promise<never> => {
  throw new Error("Chat history endpoints are deprecated. Use getShopAgentChatHistory instead.");
};

// Brand index text search - wrapper using generated SDK
// Using BrandIndexSearchResponse from generated types which includes total_returned
export type BrandIndexSearchFromTextResponse = BrandIndexSearchResponse;

export const searchBrandsIndexFromText = async (
  text: string,
  limit: number = 24,
  facets?: Array<{ name: string; values: string[] }>
): Promise<BrandIndexSearchFromTextResponse> => {
  validateBrandText(text, "text");
  const response = await shopAgentBrandIndexSearchFromText({
    client: shopAgentClient,
    body: {
      text,
      limit,
      ...(facets && facets.length > 0 && { facets }),
    },
  });

  if (!response.data) {
    throw brandSearchFailure(response.response);
  }

  return response.data as BrandIndexSearchFromTextResponse;
};

// Brand index prefix search - returns array of brand names
export const searchBrandNamesByPrefix = async (
  prefix: string,
  limit: number = 25
): Promise<string[]> => {
  validateBrandText(prefix, "prefix");
  const response = await shopAgentBrandIndexPrefixSearch({
    client: shopAgentClient,
    query: {
      prefix,
      limit,
    },
  });

  const data = response.data;
  if (!Array.isArray(data) || data.some((value) => typeof value !== "string")) {
    throw brandSearchFailure(response.response);
  }
  return data;
};

// Brand index search by brands - find similar brands with optional facets
export const searchBrandsIndexByBrands = async (
  brands: string[],
  limit: number = 24,
  facets?: Array<{ name: string; values: string[] }>
): Promise<BrandIndexSearchFromTextResponse> => {
  validateSourceBrands(brands);
  const response = await shopAgentBrandIndexSearchByBrands({
    client: shopAgentClient,
    body: {
      brands,
      limit,
      ...(facets && facets.length > 0 && { facets }),
    },
  });

  if (!response.data) {
    throw brandSearchFailure(response.response);
  }

  return response.data as BrandIndexSearchFromTextResponse;
};

// Re-export SSE event types for consumers
export type {
  ShopAgentEndEvent,
  ShopAgentErrorEvent,
  ShopAgentMessageEvent,
  ShopAgentMetadataEvent,
  ShopAgentSearchEvent,
  ShopAgentToolEvent,
};

// Discriminated union of all SSE event data payloads
export type ShopAgentStreamEventData =
  | ShopAgentMetadataEvent
  | ShopAgentToolEvent
  | ShopAgentSearchEvent
  | ShopAgentMessageEvent
  | ShopAgentEndEvent
  | ShopAgentErrorEvent;

// SSE envelope with event name + typed data
export interface CosimoStreamEvent {
  type: "metadata" | "tool" | "search" | "message" | "end" | "error";
  data?: ShopAgentStreamEventData;
}

// Type guards for runtime safety
export function isMetadataEvent(
  data: ShopAgentStreamEventData | undefined
): data is ShopAgentMetadataEvent {
  return data?.event_type === "metadata";
}

export function isToolEvent(
  data: ShopAgentStreamEventData | undefined
): data is ShopAgentToolEvent {
  return data?.event_type === "tool";
}

export function isSearchEvent(
  data: ShopAgentStreamEventData | undefined
): data is ShopAgentSearchEvent {
  return data?.event_type === "search";
}

export function isMessageEvent(
  data: ShopAgentStreamEventData | undefined
): data is ShopAgentMessageEvent {
  return data?.event_type === "message";
}

export function isEndEvent(data: ShopAgentStreamEventData | undefined): data is ShopAgentEndEvent {
  return data?.event_type === "end";
}

export function isErrorEvent(
  data: ShopAgentStreamEventData | undefined
): data is ShopAgentErrorEvent {
  return data?.event_type === "error";
}

// Cosimo agent streaming endpoint
// Returns an async generator that yields Server-Sent Events
export interface CosimoStreamRequest {
  message: string;
  threadId?: string;
  category?: CategoryToken | null;
  selectedSearchId?: string | null;
}

/**
 * Helper to parse JSON with fallback for malformed responses.
 * Handles double curly braces and other common issues.
 */
function parseSSEData(dataStr: string, eventType: string): ShopAgentStreamEventData | undefined {
  if (!dataStr) return undefined;

  // Try direct parse first
  try {
    const parsed = JSON.parse(dataStr);
    // Ensure event_type is set (backend should include it, but be defensive)
    if (!parsed.event_type) {
      parsed.event_type = eventType;
    }
    return parsed as ShopAgentStreamEventData;
  } catch {
    // Fallback: handle double curly braces (known issue with some responses)
    const cleaned = dataStr.replace(/\{\{/g, "{").replace(/\}\}/g, "}");
    try {
      const parsed = JSON.parse(cleaned);
      if (!parsed.event_type) {
        parsed.event_type = eventType;
      }
      return parsed as ShopAgentStreamEventData;
    } catch {
      console.warn("[streamCosimoChat] Failed to parse SSE data:", dataStr);
      return undefined;
    }
  }
}

/**
 * Parse CosimoAgentResponse from raw JSON string.
 * Handles common parsing issues like double curly braces.
 * Used by ChatContext and other consumers to parse message content.
 */
export function parseCosimoAgentResponse(raw: string): CosimoAgentResponse | null {
  if (!raw) return null;

  // Try direct parse first
  try {
    return JSON.parse(raw) as CosimoAgentResponse;
  } catch {
    // Fallback: handle double curly braces
    const cleaned = raw.replace(/\{\{/g, "{").replace(/\}\}/g, "}");
    try {
      return JSON.parse(cleaned) as CosimoAgentResponse;
    } catch (e) {
      console.error("[parseCosimoAgentResponse] Failed to parse:", e);
      return null;
    }
  }
}

export async function* streamCosimoChat({
  message,
  threadId,
  category,
  selectedSearchId,
}: CosimoStreamRequest): AsyncGenerator<CosimoStreamEvent, void, unknown> {
  const baseUrl = getAgentApiHost();
  const url = new URL("/shop-agent/cosimo-agent/stream", baseUrl);

  // Build auth headers (handles both production and local backend modes)
  const headers = await buildAuthHeaders(baseUrl);

  const payload: Record<string, unknown> = {
    message,
  };

  if (threadId) {
    payload.thread_id = threadId;
  }

  if (category !== undefined && category !== null) {
    if (isCategoryToken(category)) {
      payload.category = category;
    } else {
      console.warn("[streamCosimoChat] Ignoring invalid category token", category);
    }
  }

  if (selectedSearchId) {
    payload.selected_search_id = selectedSearchId;
  }

  const response = await sessionFetch(url.toString(), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Cosimo stream error: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error("Cosimo stream error: No response body");
  }

  const streamToken = new Headers(headers).get("Authorization")?.replace(/^Bearer /, "");
  const streamUid = streamToken ? decodeFirebaseJwt(streamToken)?.uid : undefined;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      assertCurrentBrowserSession(streamUid);

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      let currentEventType: CosimoStreamEvent["type"] | null = null;

      for (const line of lines) {
        if (line.startsWith("event:")) {
          currentEventType = line.slice(6).trim() as CosimoStreamEvent["type"];
        } else if (line.startsWith("data:")) {
          const dataStr = line.slice(5).trim();

          if (currentEventType) {
            const data = parseSSEData(dataStr, currentEventType);
            yield {
              type: currentEventType,
              data,
            };
            currentEventType = null;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// Search queries wrappers - returns persisted search queries for recovery
export const getUserSearchQueries = async (
  limit?: number,
  offset?: number
): Promise<SearchQueryListResponse> => {
  const response = await listUserSearchQueriesShopAgentSearchQueriesGet({
    client: shopAgentClient,
    query: { limit: limit || 50, offset: offset || 0 },
  });

  if (!response.data) {
    throw new Error("No search queries data received");
  }

  return response.data;
};

/**
 * Get the most recent search query for a thread.
 * This is the primary function for recovering search state when returning to a results page.
 *
 * @param threadId - The thread identifier
 * @returns SearchQueryItem with the most recent query, or null if not found (404)
 * @throws Error for non-404 errors (network issues, server errors, etc.)
 */
export const getThreadLatestSearchQuery = async (
  threadId: string
): Promise<SearchQueryItem | null> => {
  try {
    const response = await getThreadLatestQueryShopAgentSearchQueriesThreadsThreadIdLatestGet({
      client: shopAgentClient,
      path: { thread_id: threadId },
    });

    if (!response.data) {
      console.debug("No search query found for thread:", threadId);
      return null;
    }

    return response.data;
  } catch (error) {
    // Distinguish 404 (expected for threads without saved queries) from other errors
    // Check for 404 status in various error formats
    const is404 =
      (error instanceof Error && error.message.includes("404")) ||
      (typeof error === "object" &&
        error !== null &&
        "status" in error &&
        (error as { status?: number }).status === 404) ||
      (typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { status?: number } }).response === "object" &&
        (error as { response?: { status?: number } }).response?.status === 404);

    if (is404) {
      // 404 is expected for threads without saved queries - silently return null
      console.debug("No search query found for thread (404):", threadId);
      return null;
    }

    // Log and re-throw non-404 errors so callers can handle them appropriately
    console.error("Failed to get latest search query for thread:", threadId, error);
    throw error;
  }
};

/**
 * Retrieve a persisted search query by its search_id.
 *
 * This is used for recovery when session storage is unavailable (e.g., after
 * clearing browser data or opening a search link in a new browser/incognito).
 *
 * Callers read it through `lookupStoredSearch` (`@/lib/stored-search-lookup`),
 * which asks once per search per page load.
 *
 * @param searchId - The unique search identifier from the URL
 * @returns The stored search (a `/v1` request), or null on 404: a search that
 *   was never filed (curated, brand-page and guest searches) is an expected miss
 * @throws Error for any other status, and for network errors
 */
export const getSearchQueryById = async (searchId: string): Promise<StoredSearch | null> => {
  const response = await getSearchQueryShopAgentSearchQueriesSearchIdGet({
    client: shopAgentClient,
    path: { search_id: searchId },
  });

  if (response.data) {
    return response.data;
  }
  if (response.response?.status === 404) {
    return null;
  }
  console.warn("Search query lookup failed:", searchId, response.response?.status);
  throw new Error("Failed to get search query");
};

export const getShopAgentChatHistory = async (threadId: string): Promise<ChatHistory> => {
  const response = await getShopAgentChatHistoryShopAgentThreadsShopAgentThreadIdGet({
    client: shopAgentClient,
    path: { thread_id: threadId },
  });

  if (!response.data) {
    throw new Error("No chat history found for thread");
  }

  return response.data;
};

// Cosimo Agent (Chat) thread management - for chat conversations
export const getCosimoAgentThreads = async (): Promise<Thread[]> => {
  const response = await getCosimoAgentThreadsShopAgentThreadsCosimoAgentGet({
    client: shopAgentClient,
  });

  if (!response.data) {
    throw new Error("No chat threads data received");
  }

  return response.data;
};

export const deleteCosimoAgentThread = async (threadId: string): Promise<void> => {
  await deleteCosimoAgentThreadShopAgentThreadsCosimoAgentThreadIdDelete({
    client: shopAgentClient,
    path: { thread_id: threadId },
  });
};

// User management wrappers
export const createUser = async (body: UserCreate): Promise<User> => {
  const response = await createUserShopAgentUsersMePost({
    client: shopAgentClient,
    body,
  });

  if (!response.data) {
    throw new Error("Failed to create user");
  }

  return response.data;
};

export const getUser = async (): Promise<User> => {
  const response = await getUserShopAgentUsersMeGet({
    client: shopAgentClient,
  });

  if (!response.data) {
    throw Object.assign(new Error("User profile unavailable"), {
      status: response.response.status,
    });
  }

  return response.data;
};

export const updateUser = async (body: UserUpdate): Promise<User> => {
  const response = await updateUserShopAgentUsersMePatch({
    client: shopAgentClient,
    body,
  });

  if (!response.data) {
    throw new Error("Failed to update user");
  }

  return response.data;
};

// Update user preference arrays (e.g., tops, bottoms, shoes, brands)
export const updateUserPreferences = async (
  preferenceType: "tops" | "bottoms" | "shoes" | "brands",
  items: string[],
  action: "add" | "remove"
): Promise<User> => {
  const response = await updateUserPreferencesShopAgentUsersMePreferencesPreferenceTypePatch({
    client: shopAgentClient,
    path: { preference_type: preferenceType },
    body: { items, action } as UserPreferenceUpdate,
  });

  if (!response.data) {
    throw new Error("Failed to update user preferences");
  }

  return response.data;
};

// Get user stats

// MCP clients holding a live Cosimo grant for the caller (OCT-4250)
export const listConnectedApps = async (): Promise<ConnectedApp[]> => {
  const response = await listConnectedAppsShopAgentMcpConnectedAppsGet({
    client: shopAgentClient,
  });

  if (!response.data) {
    throw new Error("Failed to load connected apps");
  }

  return response.data;
};

// Resolves once the client holds no live grant; 404 means it already held none.
export const revokeConnectedApp = async (clientId: string): Promise<void> => {
  // CIMD client IDs are URLs, so the ID travels in the query string.
  const { response } = await revokeConnectedAppShopAgentMcpConnectedAppsDelete({
    client: shopAgentClient,
    query: { client_id: clientId },
  });

  if (!response.ok && response.status !== 404) {
    throw new Error(`Failed to revoke connected app (${response.status})`);
  }
};

// Collection management wrappers
export type PublicCollection = Omit<Collection, "user_id"> & { user_id?: string | null };

export const createCollection = async (body: CollectionCreate): Promise<Collection> => {
  const response = await createCollectionShopAgentCollectionsPost({
    client: shopAgentClient,
    body,
  });

  if (!response.data) {
    throw new Error("Failed to create collection");
  }

  return response.data;
};

export const deleteCollection = async (collectionId: string): Promise<void> => {
  await deleteCollectionShopAgentCollectionsCollectionIdDelete({
    client: shopAgentClient,
    path: { collection_id: collectionId },
  });
};

export const getCollection = async (collectionId: string): Promise<Collection> => {
  const response = await getCollectionShopAgentCollectionsCollectionIdGet({
    client: shopAgentClient,
    path: { collection_id: collectionId },
  });

  if (!response.data) {
    throw new Error("Collection not found");
  }

  return response.data;
};

export const getPublicCollection = async (collectionId: string): Promise<PublicCollection> => {
  const baseUrl = getAgentApiHost();
  const response = await fetch(`${baseUrl}/shop-agent/collections/public/${collectionId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Public collection fetch failed: ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as PublicCollection;
};

export const updateCollection = async (
  collectionId: string,
  body: CollectionUpdate
): Promise<Collection> => {
  const response = await updateCollectionShopAgentCollectionsCollectionIdPatch({
    client: shopAgentClient,
    path: { collection_id: collectionId },
    body,
  });

  if (!response.data) {
    throw new Error("Failed to update collection");
  }

  return response.data;
};

export const listCollectionsForUser = async (): Promise<Collection[]> => {
  const response = await listCollectionsForUserShopAgentCollectionsGet({
    client: shopAgentClient,
  });

  return response.data || [];
};

export const createSavedBrand = async (body: SavedBrandCreate): Promise<SavedBrand> => {
  const response = await createSavedBrandShopAgentSavedBrandsPost({
    client: shopAgentClient,
    body,
  });

  if (!response.data) {
    throw new Error("Failed to create saved brand");
  }

  return response.data;
};

export const deleteSavedBrand = async (savedBrandId: string): Promise<void> => {
  await deleteSavedBrandShopAgentSavedBrandsSavedBrandIdDelete({
    client: shopAgentClient,
    path: { saved_brand_id: savedBrandId },
  });
};

export const getSavedBrandByName = async (brandName: string): Promise<SavedBrand | null> => {
  const response = await getSavedBrandByNameShopAgentSavedBrandsByNameBrandNameGet({
    client: shopAgentClient,
    path: { brand_name: brandName },
  });

  return response.data ?? null;
};

export const updateSavedBrand = async (
  savedBrandId: string,
  body: SavedBrandUpdate
): Promise<SavedBrand> => {
  const response = await updateSavedBrandShopAgentSavedBrandsSavedBrandIdPatch({
    client: shopAgentClient,
    path: { saved_brand_id: savedBrandId },
    body,
  });

  if (!response.data) {
    throw new Error("Failed to update saved brand");
  }

  return response.data;
};

export const listSavedBrandsForUser = async (): Promise<SavedBrand[]> => {
  const response = await listSavedBrandsForUserShopAgentSavedBrandsGet({
    client: shopAgentClient,
  });

  return response.data || [];
};

export const createSavedSearch = async (body: SavedSearchCreate): Promise<SavedSearch> => {
  const response = await createSavedSearchShopAgentSavedSearchesPost({
    client: shopAgentClient,
    body,
  });

  if (!response.data) {
    throw new Error("Failed to create saved search");
  }

  return response.data;
};

export const deleteSavedSearch = async (savedSearchId: string): Promise<void> => {
  await deleteSavedSearchShopAgentSavedSearchesSavedSearchIdDelete({
    client: shopAgentClient,
    path: { saved_search_id: savedSearchId },
  });
};

export const updateSavedSearch = async (
  savedSearchId: string,
  body: SavedSearchUpdate
): Promise<SavedSearch> => {
  const response = await updateSavedSearchShopAgentSavedSearchesSavedSearchIdPatch({
    client: shopAgentClient,
    path: { saved_search_id: savedSearchId },
    body,
  });

  if (!response.data) {
    throw new Error("Failed to update saved search");
  }

  return response.data;
};

export const listSavedSearchesForUser = async (): Promise<SavedSearch[]> => {
  const response = await listSavedSearchesForUserShopAgentSavedSearchesGet({
    client: shopAgentClient,
  });

  return response.data || [];
};

// Cart management wrappers (legacy - use getUserCarts and updateCart instead)
export const getCart = async (): Promise<Cart> => {
  // Get the first cart from user's carts (default cart)
  const carts = await getUserCarts();
  if (carts.length === 0) {
    throw new Error("No cart found for user");
  }
  return carts[0];
};

export const replaceCart = async (body: CartUpsert): Promise<Cart> => {
  // Get the first cart and update it
  const carts = await getUserCarts();
  if (carts.length === 0) {
    throw new Error("No cart found for user");
  }
  return updateCart(carts[0].id, body.product_ids, body.product_variants || undefined);
};

// Re-export VariantSpec for backwards compatibility
export type { VariantSpec };

// Cart management wrapper functions
export const getUserCarts = async (): Promise<Cart[]> => {
  const response = await getUserCartsShopAgentCartsGet({
    client: shopAgentClient,
  });

  return response.data || [];
};

export const createCart = async (
  name?: string,
  productIds?: string[],
  productVariants?: VariantSpec[]
): Promise<Cart> => {
  const response = await createCartShopAgentCartsPost({
    client: shopAgentClient,
    body: {
      name,
      product_ids: productIds || [],
      product_variants: productVariants || null,
    },
  });

  if (!response.data) {
    throw new Error("Failed to create cart");
  }

  return response.data;
};

export const getCartById = async (cartId: string): Promise<Cart> => {
  const response = await getCartShopAgentCartsCartIdGet({
    client: shopAgentClient,
    path: { cart_id: cartId },
  });

  if (!response.data) {
    throw new Error("Cart not found");
  }

  return response.data;
};

export const updateCart = async (
  cartId: string,
  productIds: string[],
  productVariants?: VariantSpec[]
): Promise<Cart> => {
  const response = await updateCartShopAgentCartsCartIdPut({
    client: shopAgentClient,
    path: { cart_id: cartId },
    body: {
      product_ids: productIds,
      product_variants: productVariants || null,
    },
  });

  if (!response.data) {
    throw new Error("Failed to update cart");
  }

  return response.data;
};

export const addItemsToCart = async (
  cartId: string,
  productIds: string[],
  productVariants?: VariantSpec[]
): Promise<Cart> => {
  const response = await addItemsToCartShopAgentCartsCartIdItemsPost({
    client: shopAgentClient,
    path: { cart_id: cartId },
    body: {
      product_ids: productIds,
      product_variants: productVariants || null,
    },
  });

  if (!response.data) {
    throw new Error("Failed to add items to cart");
  }

  return response.data;
};

export const removeItemsFromCart = async (cartId: string, productIds: string[]): Promise<Cart> => {
  const response = await removeItemsFromCartShopAgentCartsCartIdItemsDelete({
    client: shopAgentClient,
    path: { cart_id: cartId },
    body: { product_ids: productIds },
  });

  if (!response.data) {
    throw new Error("Failed to remove items from cart");
  }

  return response.data;
};

// Image enrichment wrapper
// Note: shopAgentEnrichImage endpoint has been removed from the API
export const enrichImage = async (
  _imageId: string,
  _description?: string | null
): Promise<ProductEnrichment> => {
  console.warn("enrichImage: Image enrichment endpoint not available in current API");
  throw new Error("Image enrichment endpoint not available in current API");
};

// ==================== Attribute Agent API Wrappers ====================

// Re-export types for external use
export type { AttributeCount, AttributeSuggestion };

/**
 * Suggests product attributes based on search results.
 * Uses Gemini 2.5 Flash to analyze attribute occurrence counts
 * and suggest the 5 most useful attributes for refining a search.
 *
 * @param attributes - Array of attributes with their occurrence counts
 * @returns Array of attribute suggestions with recommended values
 */
export const suggestAttributes = async (
  attributes: AttributeCount[]
): Promise<AttributeSuggestion[]> => {
  const response = await suggestAttributesShopAgentAttributeAgentSuggestPost({
    client: shopAgentClient,
    body: { attributes },
  });

  if (!response.data) {
    throw new Error("Failed to suggest attributes");
  }

  return response.data.items;
};

// ==================== Catalog API Wrappers ====================

/**
 * The shopper search route refused a cursor it did not mint for /v1 (one
 * minted before shop-agent served every surface from /v1). The search
 * restarts from page 1.
 */
export class InvalidSearchCursorError extends Error {
  constructor() {
    super("invalid cursor");
    this.name = "InvalidSearchCursorError";
  }
}

// A shopper search on a /v1 request, answered by Platform /v1. The `surface`
// (default `results_page`) labels shop-agent's /v1 calls.
export const searchShopperProducts = async (
  body: ShopperSearchRequest,
  options?: { threadId?: string; searchId?: string; surface?: ShopperSearchSurface }
): Promise<ShopperSearchPage> => {
  const response = await shopAgentSearchProducts({
    client: shopAgentClient,
    body,
    query: {
      surface: options?.surface ?? "results_page",
      ...(options?.threadId ? { thread_id: options.threadId } : {}),
      ...(options?.searchId ? { search_id: options.searchId } : {}),
    },
  });

  if (response.data) {
    return response.data;
  }
  const detail = (response.error as { detail?: unknown } | undefined)?.detail;
  if (response.response?.status === 400 && detail === "invalid cursor") {
    throw new InvalidSearchCursorError();
  }
  throw new Error("Failed to search products");
};

// The page 1 the chat agent's search cached under `searchId`; null when
// nothing is cached (an older search, or one past the cache's lifetime).
export const getShopperSearchPage = async (searchId: string): Promise<ShopperSearchPage | null> => {
  const response = await shopAgentGetSearchPage({
    client: shopAgentClient,
    path: { search_id: searchId },
  });

  if (response.data) {
    return response.data;
  }
  if (response.response?.status === 404) {
    return null;
  }
  throw new Error("Failed to get search page");
};

// Similar products (PDP and collection rails, the "More like this" modal):
// one /v1 more-like-this page.
export const searchSimilarProducts = async (
  request: SimilarProductsRequest
): Promise<SimilarProductsResponse> => {
  const response = await shopAgentSearchSimilarProducts({
    client: shopAgentClient,
    body: request,
  });

  if (response.data) {
    return response.data;
  }
  const detail = (response.error as { detail?: unknown } | undefined)?.detail;
  if (response.response?.status === 400 && detail === "invalid cursor") {
    throw new InvalidSearchCursorError();
  }
  throw new Error("Failed to fetch similar products");
};

export const getCategoryCounts = async (
  request: CategoryCountsRequest
): Promise<CategoryCountsResponse> => {
  const response = await getCategoryCountsShopAgentCategoryAttributesCategoryCountsPost({
    client: shopAgentClient,
    body: request,
  });

  if (!response.data) {
    throw new Error("Failed to get category counts");
  }

  return response.data;
};

export type CuratedResultType = "product" | "brand";

export interface PublishedCuratedSearchInfo {
  id: string;
  handle: string;
  title: string;
  description?: string | null;
  result_type: CuratedResultType;
  query_kind: "product_text_search" | "brand_text";
  revision: number;
  placement: string;
  sort_order: number;
  display_metadata?: Record<string, unknown>;
}

export interface CuratedHomeProductRail {
  query: CuratedQuery;
  search: {
    /** `/v1` list items; render with `productFromListItem`. */
    products: MerchantProductListItem[];
    total_found: number;
  };
}

export interface CuratedHomeBrandRail {
  query: PublishedCuratedSearchInfo;
  search: BrandIndexSearchResponse;
}

export interface CuratedHomeRailsResponse {
  product_rails: CuratedHomeProductRail[];
  brand_rails: CuratedHomeBrandRail[];
}

export const fetchHomeCuratedRails = async (
  placement: string = "home_curated_rails"
): Promise<CuratedHomeRailsResponse> => {
  const response = await shopAgentClient.get({
    url: "/shop-agent/search/curated/home",
    query: { placement },
  });

  if (!response.data) {
    throw new Error("Failed to fetch home curated rails");
  }

  return response.data as CuratedHomeRailsResponse;
};

// Get taxonomy - fetches from CDN
export const getTaxonomy = async (): Promise<TaxonomyTreeResponse> => {
  const data = await getTaxonomyAndAttributes();
  return data.taxonomy;
};

// Get attributes - fetches from CDN
export const getAttributes = async (): Promise<AttributesDataResponse> => {
  const data = await getTaxonomyAndAttributes();
  if (!data.attributes) {
    throw new Error("Attributes not found in taxonomy data");
  }
  return data.attributes;
};

// Get taxonomy and attributes - replaces catalogGetTaxonomyAndAttributes
export const getTaxonomyAndAttributes = async (): Promise<TaxonomyAndAttributesResponse> => {
  try {
    const response = await fetch(
      "https://cdn.storerouter.ai/catalog/taxonomy_and_attributes.json",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to get taxonomy and attributes: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data as TaxonomyAndAttributesResponse;
  } catch (error) {
    throw new Error(
      `Failed to get taxonomy and attributes: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
};

// Get aggregated attributes using facets (preferred)
export const getAttributesByFacets = async (
  facets: CategoryAttributesByFacetsRequest["facets"]
): Promise<GetAttributeValuesResponse> => {
  const response =
    await getAttributesByFacetsShopAgentCategoryAttributesAggregatedAttributesByFacetsPost({
      client: shopAgentClient,
      body: { facets },
    });

  if (!response.data) {
    throw new Error("Failed to get aggregated attributes by facets");
  }

  return response.data;
};

// Get product by UUID - returns SearchProduct directly from the API
export const hydrateProductByUuid = async (productUuid: string): Promise<ProductDetailResponse> => {
  const response = await shopAgentCatalogGetProductGroupByUuid({
    client: shopAgentClient,
    query: { product_uuid: productUuid },
  });

  if (!response.data) {
    throw new Error(`Failed to hydrate product – no data returned for uuid: ${productUuid}`);
  }

  return response.data;
};

/**
 * Hydrate saved product ids (cart, collections, saved items): one result per
 * id, in request order, with one batched request per 50 ids. A missing product
 * is a result without `product`; a failed request rejects the whole call.
 */
export const hydrateProducts = async (productUuids: string[]): Promise<HydratedProduct[]> => {
  const { hydrateInBatches } = await import("./saved-product-hydration");
  return hydrateInBatches(productUuids, async (uuids) => {
    const response = await shopAgentCatalogHydrateProducts({
      client: shopAgentClient,
      body: { uuids },
    });
    if (!response.data) {
      throw new Error(`Failed to hydrate ${uuids.length} products`);
    }
    return response.data.results;
  });
};

// Hydrate saved products, dropping the ones no longer available
export const getCatalogProducts = async (productUuids: string[]): Promise<SearchProduct[]> => {
  const { hydrateSavedProducts } = await import("./saved-product-hydration");
  return hydrateSavedProducts(productUuids, hydrateProducts, (uuid) => {
    console.error(`Product with uuid ${uuid} is no longer available`);
  });
};

// ==================== Brand API Wrappers ====================

// List all brand names - replaces catalogListBrands
export const listBrandNames = async (): Promise<string[]> => {
  const response = await shopAgentBrandListBrands({
    client: shopAgentClient,
  });

  if (!response.data) {
    throw new Error("Failed to list brands");
  }

  return response.data.brands;
};

/**
 * @deprecated Use searchBrandNamesByPrefix instead.
 * Temporarily forwards to prefix search to maintain compatibility.
 */
export const searchBrandNames = async (query: string, limit: number = 25): Promise<string[]> => {
  try {
    return await searchBrandNamesByPrefix(query, limit);
  } catch (error) {
    console.error("searchBrandNames (deprecated) failed, query:", query, error);
    return [];
  }
};

// Brand details type definition - re-export from generated types
export type BrandDetails = BrandIndex;

// Discovery metadata is projected from StoreRouter; native directory/content
// callers below retain their existing detailed BrandIndex contract.
export const getBrandDiscoveryDetails = async (
  brandName: string
): Promise<BrandDiscoveryDetails | null> => {
  const response = await shopAgentBrandDiscoveryGetDetails({
    client: shopAgentClient,
    path: { brand_name: brandName },
  });
  if (response.response.status === 404) return null;
  if (!response.data) throw brandSearchFailure(response.response);
  return response.data;
};

// Get brand details by name - replaces getBrandDetails
export const getBrandDetails = async (brandName: string): Promise<BrandDetails | null> => {
  try {
    const response = await shopAgentBrandGetByName({
      client: shopAgentClient,
      path: { brand_name: brandName },
    });

    if (!response.data) {
      return null;
    }

    return response.data;
  } catch (error) {
    console.error(`Failed to get brand details for ${brandName}:`, error);
    return null;
  }
};

// Get details for multiple brands - replaces getBrandDetailsMultiple
export const getBrandDetailsMultiple = async (
  brandNames: string[]
): Promise<Map<string, BrandDetails>> => {
  if (brandNames.length === 0) return new Map();

  // Deduplicate brand names
  const uniqueBrandNames = Array.from(new Set(brandNames));

  const brandPromises = uniqueBrandNames.map(async (brandName) => {
    try {
      const details = await getBrandDetails(brandName);
      return { brandName, details };
    } catch (err) {
      console.error(`Failed to get details for brand ${brandName}`, err);
      return { brandName, details: null };
    }
  });

  const results = await Promise.all(brandPromises);
  const brandMap = new Map<string, BrandDetails>();

  results.forEach(({ brandName, details }) => {
    if (details) {
      brandMap.set(brandName, details);
    }
  });

  return brandMap;
};

/** Private results-page outcome; failures remain rejected promises. */
export type ProductBrandOptionsOutcome =
  | { status: "success"; brands: string[] }
  | {
      status: "skipped";
      brands: string[];
      reason:
        | "no_brand_context"
        | "unsupported_facet"
        | "invalid_projection"
        | "budget_exceeded"
        | "unsupported_context";
    };

export const generateBrandsByQuery = async ({
  text,
  filters,
  limit,
  signal,
}: {
  text: string;
  filters: ShopperFilters;
  limit: number;
  signal?: AbortSignal;
}): Promise<ProductBrandOptionsOutcome> => {
  const response = await shopAgentBrandIndexSearchFromQuery({
    client: shopAgentClient,
    signal,
    body: {
      query: {
        text,
        limit,
        facets: filters.facets,
        exclusion_facets: filters.exclude_facets,
      },
    },
  });
  if (response.error || !response.response.ok) {
    // Never interpret auth, transport or backend errors as no matches.
    throw brandSearchFailure(response.response);
  }
  const data = response.data;
  if (
    !data ||
    !Array.isArray(data.brands) ||
    !data.brands.every((name) => typeof name === "string")
  ) {
    throw brandSearchFailure();
  }
  if (data.status === "success") return { status: "success", brands: data.brands };
  if (
    data.status === "skipped" &&
    data.brands.length === 0 &&
    [
      "no_brand_context",
      "unsupported_facet",
      "invalid_projection",
      "budget_exceeded",
      "unsupported_context",
    ].includes(data.reason)
  ) {
    return { status: "skipped", brands: [], reason: data.reason };
  }
  throw brandSearchFailure();
};

// Re-export top categories types from generated types
export type { BrandTopCategoriesResponse, BrandTopCategory };

// Re-export shopping options types from generated types
export type { BrandShoppingOptionsResponse };

// Get shopping options/eligibility for a brand (Men's, Women's, Kids, Beauty)
export const getBrandShoppingOptions = async (
  brandName: string
): Promise<BrandShoppingOptionsResponse> => {
  const response = await shopAgentBrandGetShoppingOptions({
    client: shopAgentClient,
    path: { brand_name: brandName },
  });

  if (!response.data) {
    throw new Error("Failed to get brand shopping options");
  }

  return response.data;
};

// Similar brands response type
export interface SimilarBrandsResponse {
  results: Array<{
    brand_name?: string;
    brand_slug?: string;
    brand_uuid?: string;
    combined_score?: number;
    [key: string]: unknown;
  }>;
}

// File upload response type
export interface FileUploadResponse {
  file_id: string;
  file_url?: string;
  [key: string]: unknown;
}

// Placeholder for file upload - uploadImageToCatalog equivalent

export const uploadImageToCatalog = async (_file: File): Promise<FileUploadResponse> => {
  console.warn("uploadImageToCatalog: File upload endpoints not yet available in shop_agent API");
  throw new Error("File upload not yet implemented in shop_agent API");
};

// Clear brand cache function (no-op for now)
export const clearBrandDetailsCache = (): void => {
  // No-op for now since we don't have brand caching yet
};

// ==================== Taxonomy Path Resolution ====================

// Cache taxonomy data in memory to avoid repeated fetches
let cachedTaxonomyData: TaxonomyAndAttributesResponse | null = null;

export interface DisplayPathResult {
  path: string; // "Womenswear > Clothing > Dresses"
  segments: string[]; // ["Womenswear", "Clothing", "Dresses"]
  canonicalPath: string; // "Adult Clothing > Clothing > Dresses" (actual taxonomy path)
}

/**
 * Converts search facets to display breadcrumb path.
 * Used for showing breadcrumbs in search results.
 *
 * @param facets - Array of facets from search query
 * @returns Display path result with formatted path string, display segments, and canonical path
 */
export async function resolveFacetsToDisplayPath(
  facets: Array<{ name: string; values: string[] }>
): Promise<DisplayPathResult | null> {
  // Load and cache taxonomy data
  if (!cachedTaxonomyData) {
    cachedTaxonomyData = await getTaxonomyAndAttributes();
  }

  if (!facets || facets.length === 0) {
    return null;
  }

  // Extract gender facet
  const genderFacet = facets.find((f) => f.name === "gender" && f.values?.length > 0);
  const genderValue = genderFacet ? String(genderFacet.values[0]).toLowerCase() : null;

  // Extract activewear facet
  const activewearFacet = facets.find((f) => f.name === "is_activewear" && f.values?.length > 0);
  const isActivewear =
    activewearFacet && String(activewearFacet.values[0]).toLowerCase() === "true";

  // Extract category facets sorted by depth
  const categoryFacets = facets
    .filter((f) => f.name.startsWith("category_path.depth_"))
    .map((f) => {
      const depthMatch = f.name.match(/depth_(\d+)/);
      const depth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
      return { depth, name: f.name, values: f.values };
    })
    .sort((a, b) => a.depth - b.depth);

  if (categoryFacets.length === 0) {
    return null;
  }

  // Build display path
  const segments: string[] = [];

  // Add root/lane based on depth_0 and gender
  const depth0 = categoryFacets.find((f) => f.depth === 0);
  if (depth0 && depth0.values?.length > 0) {
    const rootValue = String(depth0.values[0]).toLowerCase();
    // Check for kids first (before adult) since "kids clothing" contains "clothing"
    if (rootValue.includes("kid")) {
      // Kids clothing - use gender-specific lane
      if (genderValue === "men") {
        segments.push("Boys");
      } else if (genderValue === "women") {
        segments.push("Girls");
      } else {
        segments.push("Kids");
      }
    } else if (rootValue.includes("beauty")) {
      segments.push("Beauty");
    } else if (rootValue.includes("adult") || rootValue.includes("clothing")) {
      // Adult clothing - use gender-specific lane
      if (genderValue === "men") {
        segments.push("Menswear");
      } else if (genderValue === "women") {
        segments.push("Womenswear");
      } else {
        segments.push("Adult");
      }
    }
  }

  // Check if we need to insert "Activewear" segment
  const shouldInsertActivewear =
    isActivewear &&
    categoryFacets.length > 1 &&
    (segments[0] === "Menswear" || segments[0] === "Womenswear");

  // Add department and subcategories
  for (let i = 1; i < categoryFacets.length; i++) {
    const facet = categoryFacets[i];
    if (facet.values?.length > 0) {
      const value = String(facet.values[0]);

      // Insert "Activewear" before first subcategory if needed
      if (shouldInsertActivewear && i === 1 && value.toLowerCase() === "clothing") {
        segments.push("Activewear");
      }

      // Capitalize first letter for display
      const displayValue = value.charAt(0).toUpperCase() + value.slice(1);
      segments.push(displayValue);
    }
  }

  if (segments.length === 0) {
    return null;
  }

  // Build canonical path from actual facet values (for API calls)
  const canonicalPath = buildCanonicalPathFromFacets(facets);

  return {
    path: segments.join(" > "),
    segments,
    canonicalPath,
  };
}

/**
 * Helper functions for converting display paths to canonical taxonomy paths
 * Shared by the search filter components
 */
export function mapLaneToRoot(label: string): string {
  const s = String(label || "")
    .trim()
    .toLowerCase();
  if (s === "menswear" || s === "womenswear" || s === "adult") return "Adult Clothing";
  if (s === "beauty") return "Beauty";
  if (s.startsWith("kid") || s === "boys" || s === "girls") return "Kid's Clothing";
  // Already a taxonomy root name
  return label;
}

export function capitalizeSegment(segment: string): string {
  // Handle special cases
  if (segment.toLowerCase() === "jumpsuits & sets") return "Jumpsuits & Sets";
  if (segment.toLowerCase() === "jackets & coats") return "Jackets & Coats";

  // General capitalization
  return segment
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Converts display path (from breadcrumbs/navigation) to canonical taxonomy path
 * for use with category-attributes API.
 *
 * @param displayPath - Array like ["Womenswear", "Clothing", "Dresses"]
 * @returns Canonical path like "Adult Clothing > Clothing > Dresses"
 */
export function convertDisplayPathToCanonical(displayPath: string[]): string {
  if (!displayPath || displayPath.length === 0) return "";

  const root = mapLaneToRoot(displayPath[0]);
  const path = [root, ...displayPath.slice(1).map(capitalizeSegment)];
  return path.join(" > ");
}

/**
 * Builds canonical taxonomy path from category facets.
 * Reuses the display path building logic from resolveFacetsToDisplayPath
 * but returns the canonical version for API calls.
 *
 * @param facets - Array of facets from search query
 * @returns Canonical path like "Adult Clothing > Clothing > Dresses"
 */
export function buildCanonicalPathFromFacets(
  facets: Array<{ name: string; values: string[] }>
): string {
  const categoryFacets = facets
    .filter((f) => f.name.startsWith("category_path.depth_"))
    .map((f) => {
      const depthMatch = f.name.match(/depth_(\d+)/);
      const depth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
      return { depth, values: f.values };
    })
    .sort((a, b) => a.depth - b.depth);

  const segments: string[] = [];
  for (const facet of categoryFacets) {
    if (facet.values?.length > 0) {
      const value = String(facet.values[0]);
      // Capitalize properly to match taxonomy format
      const canonicalValue = capitalizeSegment(value);
      segments.push(canonicalValue);
    }
  }

  return segments.join(" > ");
}
